/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from "react";
import Card from "@mui/material/Card";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import toast from "react-hot-toast";
import { CompanyService } from "services/companyService";
import AccountingStatement6Service from "services/accounting-statement-6";
import { formatVndIntegerValue } from "utils";
import DataTable from "./DataTable/index";
import ActionCell from "./components/ActionCell/index";

const currentYear = String(new Date().getFullYear());
const currentMonth = String(new Date().getMonth() + 1);

const tableData = {
  columns: [
    { Header: "Tháng", accessor: "month", align: "center" },
    { Header: "Năm", accessor: "year", align: "center" },
    { Header: "Đơn vị", accessor: "unit" },
    { Header: "Tổng dư đầu", accessor: "openingBalance", align: "right" },
    { Header: "Tổng phát sinh", accessor: "increaseAmount", align: "right" },
    { Header: "Tổng phân bổ", accessor: "totalAllocationAmount", align: "right" },
    { Header: "Tổng dư cuối", accessor: "closingBalance", align: "right" },
    { Header: "Thao tác", accessor: "action", sorted: false, align: "center" },
  ],
  rows: [],
};

function AccountingTable6() {
  const [companyId, setCompanyId] = useState("");
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [dataTableState, setDataTableState] = useState(tableData);
  const [filters, setFilters] = useState({
    year: currentYear,
    month: currentMonth,
    fromDate: "",
    toDate: "",
    searchBy: "",
  });
  const [query, setQuery] = useState({
    year: currentYear,
    month: currentMonth,
    fromDate: "",
    toDate: "",
    searchBy: "",
  });
  const [paginationData, setPaginationData] = useState({ page: 1, count: 0, size: 10 });

  useEffect(() => {
    const init = async () => {
      const response = await CompanyService.getByAuth();
      const payload = response?.data || response || {};
      setCompanyId(payload?._id || "");
      setCompanyData(payload);
    };
    init();
  }, []);

  const fetchData = useCallback(async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      const response = await AccountingStatement6Service.findAll({
        company: companyId,
        year: query.year,
        month: query.month,
        fromDate: query.fromDate || undefined,
        toDate: query.toDate || undefined,
        searchBy: query.searchBy || undefined,
        page: paginationData.page,
        limit: paginationData.size,
      });
      const payload = response?.data || response || {};
      
      setPaginationData((prev) => ({
        ...prev,
        count: payload.paginate?.count || 0,
        size: payload.paginate?.size || prev.size,
      }));

      const rows = (payload.items || []).map((item) => {
        return {
          month: item.month,
          year: item.year,
          unit: item.unit,
          openingBalance: formatVndIntegerValue(item.totals?.openingBalance || 0),
          increaseAmount: formatVndIntegerValue(item.totals?.increaseAmount || 0),
          totalAllocationAmount: formatVndIntegerValue(item.totals?.totalAllocationAmount || 0),
          closingBalance: formatVndIntegerValue(item.totals?.closingBalance || 0),
          action: <ActionCell item={{ viewLink: `/templates/accounting-table-6/detail/${item._id}` }} />,
        };
      });

      setDataTableState((prev) => ({ ...prev, rows }));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Không thể tải Bảng kê số 6");
      setDataTableState((prev) => ({ ...prev, rows: [] }));
    } finally {
      setLoading(false);
    }
  }, [companyId, query, paginationData.page, paginationData.size]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSync = async () => {
    if (!companyId || !filters.year || !filters.month) {
      toast.error("Thiếu thông tin công ty, năm hoặc tháng");
      return;
    }
    try {
      setSyncing(true);
      
      const previewRes = await AccountingStatement6Service.preview({
        company: companyId,
        year: filters.year,
        month: filters.month,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
      });
      const previewData = previewRes?.data || previewRes || {};

      await AccountingStatement6Service.sync({
        company: companyId,
        unit: companyData?.unitName || companyData?.name || "Đơn vị",
        address: companyData?.address || "",
        year: filters.year,
        month: filters.month,
        debitAccountLabels: previewData.debitAccountLabels,
        rows: previewData.rows,
        totals: previewData.totals,
      });

      toast.success("Đồng bộ dữ liệu Bảng kê số 6 thành công");
      setQuery((prev) => ({ ...prev, ...filters }));
      setPaginationData((prev) => ({ ...prev, page: 1 }));
      await fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Đồng bộ dữ liệu thất bại");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <Card style={{ height: "100%" }}>
          <DataTable
            table={dataTableState}
            loading={loading}
            syncing={syncing}
            filters={filters}
            setFilters={setFilters}
            query={query}
            setQuery={setQuery}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
            onSync={handleSync}
            onFetchData={(newFilters) => {
              setQuery((prev) => ({ ...prev, ...newFilters }));
              setPaginationData((prev) => ({ ...prev, page: 1 }));
            }}
          />
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default AccountingTable6;
